import * as sentry from '@sentry/react';
import dayjs from 'dayjs';
import {call, put, select, takeLatest} from 'redux-saga/effects';

import {config} from 'config/env';
import i18n from 'config/i18n/i18n';
import {
  BASKET_RECOMMENDATION_PAGE_SIZE,
  isBasketDiversifyFlagEnabled,
  isNewEndPoint,
  loyaltyProductPDPEnabled,
  recommendationVariant,
  shouldFetchDeliveryDescription,
  webEnableDiscontinuedItem
} from 'constants/recommendation';
import {
  getBasketRecommendations,
  getProductsByIds,
  getRecommendationPieceResultV2
} from 'services/recommendations/recommendations';
import {
  getBasketRecommendationsAction,
  setBasketRecommendationsAction,
  setIsBasketRecommendationLoadingAction
} from 'store/actions/basket-action';
import {AppState} from 'store/types/app';
import {AuthState} from 'store/types/auth';
import {BasketRecommendationRequest, Product, RecommendationResponse} from 'types';
import {getProductIdWithoutPrefix} from 'utils';

import {AppMode} from '../../constants';

const TIMERINTERVAL = 5;

function* GetBasketRecommendationsSaga(): Generator {
  try {
    yield put(setIsBasketRecommendationLoadingAction(true));

    const {idToken, customerId} = (yield select((state) => state.auth) as unknown) as AuthState;
    const {appMode} = (yield select((state) => state.app) as unknown) as AppState;
    const isExpress = appMode === AppMode.express;

    const {currentStore} = (yield select((state) => state?.app)) as AppState;
    const isSalesCustomer = false;
    const isUnavailableReasonEnabled = false;

    const enableRmsStatus = webEnableDiscontinuedItem || false;
    const is3P = false;
    const isProPointsEnabled = loyaltyProductPDPEnabled;
    const isSameDayAllowedForStore = currentStore?.isSameDayDelivery;
    const storeCodes = currentStore?.storeCodes;
    const is3PComplexPromotionEnabled = config.RECOM_LOYALTY_COMPLEX_PROMOTION_3P_ENABLED;

    const lang = i18n.language;
    const payload = {
      page: 1,
      size: BASKET_RECOMMENDATION_PAGE_SIZE,
      diversifyResults: isBasketDiversifyFlagEnabled
    } as BasketRecommendationRequest;
    const startTime = dayjs();
    const date = undefined;
    const membershipId = undefined;
    const apiResponse: any = yield call(() =>
      getBasketRecommendations(
        payload,
        idToken,
        customerId,
        isNewEndPoint,
        recommendationVariant,
        storeCodes,
        isExpress
      )
    );

    const endTime = dayjs();
    const responseTime = endTime.diff(startTime, 'seconds');
    const productIds = apiResponse?.products?.map((x: Product) => x.id);

    let recommendations = {
      products: [],
      recommendationType: '',
      totalCount: 0,
      actualCount: 0,
      algorithm: '',
      personalized: false
    } as RecommendationResponse;
    if (TIMERINTERVAL >= responseTime && productIds?.length !== 0) {
      const brandProductsData = yield call(() =>
        getProductsByIds(
          productIds,
          (storeCodes ?? []) as string[],
          lang,
          isSalesCustomer,
          enableRmsStatus,
          is3P,
          membershipId,
          shouldFetchDeliveryDescription,
          isSameDayAllowedForStore,
          date,
          isProPointsEnabled,
          isUnavailableReasonEnabled,
          isExpress,
          is3PComplexPromotionEnabled
        )
      );

      const productsDetails = getRecommendationPieceResultV2(
        apiResponse.products,
        brandProductsData as Product[]
      ) as any;
      let updateProductDetails: Product[] = productsDetails;

      updateProductDetails = productsDetails.map((productDetail: Product) => {
        const matchingProduct = apiResponse.products.find(
          (p: Product) => p.id === getProductIdWithoutPrefix(productDetail?.id)
        );
        if (matchingProduct) {
          return {
            ...productDetail,
            position: matchingProduct?.position,
            normalisedRecommendationScore: matchingProduct?.normalisedRecommendationScore,
            recommendationScore: matchingProduct?.recommendationScore,
            alreadyPurchased: matchingProduct?.alreadyPurchased
          };
        }
        return productDetail;
      });

      recommendations = {
        products: updateProductDetails || [],
        recommendationType: apiResponse.recommendationType,
        totalCount: apiResponse.totalCount,
        actualCount: productIds.length,
        algorithm: apiResponse.algorithm,
        personalized: apiResponse?.personalised
      };
    }

    // this is bad as this is causing race condition between the basket rm3 and the recommendation rm3
    // please create different action and use different state
    // yield put(getRM3PromotionsAction({ids: makroIds, isRecommendation: true}));
    yield put(setBasketRecommendationsAction(recommendations));
  } catch (e: any) {
    sentry.captureException(e);
  } finally {
    yield put(setIsBasketRecommendationLoadingAction(false));
  }
}

export default function* () {
  yield takeLatest(getBasketRecommendationsAction, GetBasketRecommendationsSaga);
}
